import {store} from '../../../redux/store';
import {
  setClinicalCase,
  setClinicalCaseIsFail,
  setClinicalCaseIsLoading,
  setCurrentThreadId,
  setMessageIsFail,
  setMessageIsLoading,
  setMessageSuccess,
  setStreamMessageLoadingId,
  setTypingStatus,
  setUserThreads,
  setUserThreadsFail,
  setUserThreadsLoading,
} from './reducer';
import {finishLoading} from '../../../connected-components/loading/actions';
import {ChatType, MessageStatus} from '../../../utils/enums';
import {services} from '../../../api';
import {QNAForm, ThreadItemType} from '../../../interfaces/api/qna';
import {throttle} from 'lodash';
import dayjs from 'dayjs';
import {
  MessageItem,
  SendMessageWithImageParams,
} from '../../../interfaces/reducers/clicicalCase';
import {
  cleanAndFormatMarkdown,
  convertUrlsToMarkdown,
} from '../../../utils/markdownHelpers';

const IS_ClinicalCase_DEBUGGING = false;

export const getUserThreads = async (search?: string, tab_id?: string): Promise<void> => {
  try {
    store.dispatch(setUserThreadsLoading());

    const res = await services.qna.getUserThreads(
      ChatType.ClinicalCase,
      search,
      tab_id,
    );
    if (!res) {
      throw new Error('');
    }
    if (res?.content) {
      store.dispatch(setUserThreads(res?.content || []));
    }
  } catch (error: any) {
    store.dispatch(setUserThreadsFail());
  }
};

export const getThreadMessages = async (
  threadId: string | number,
  refresh: boolean = false,
): Promise<void> => {
  try {
    const {id, first_name, last_name} =
      store.getState()?.auth?.currentUser || {};
    const tempData = store.getState().clinicalCase;

    if ((!refresh && tempData?.isLastPage) || tempData.threadId === threadId) {
      return;
    }

    if (refresh) {
      const isForceReset =
        tempData.data.length > 0 && tempData.data[0].thread_id !== threadId;
      if (isForceReset) {
        store.dispatch(setClinicalCase({data: []}));
      }
    }
    store.dispatch(setClinicalCaseIsLoading());
    const res = await services.qna.getThreadMessages(threadId);
    if (!res) {
      throw new Error('');
    }
    if (res?.content?.id) {
      store.dispatch(setCurrentThreadId(res?.content?.id));
    }

    let data = [...(tempData?.data || [])];
    const newData = [...(res?.content?.messages || [])].map(i => {
      return {
        message: (i.content || '') + convertUrlsToMarkdown(i.citations),
        sender_id: i.user_id + '',
        role: i.role,
        sender_name: i.user_id === id ? first_name + ' ' + last_name : 'GENIO',
        thread_id: i.thread_id || '',
        status: MessageStatus.ReadByEveryone,
        created_at: new Date(i.created_at).toISOString(),
      };
    });

    if (refresh) {
      data = newData;
    } else {
      data = [...data, ...newData];
    }

    store.dispatch(setClinicalCase({data}));
    store.dispatch(setMessageSuccess());
  } catch (error: any) {

    store.dispatch(setClinicalCaseIsFail());
  }
};

export const getLastMessageByUser = (
  messages: MessageItem[],
): MessageItem | null => {
  const {id: userId} = store.getState()?.auth?.currentUser || {};
  if (!userId || !messages || messages.length === 0) {
    return null;
  }
  const userMessages = messages
    .filter(message => message.sender_id + '' === userId + '')
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

  return userMessages.length > 0 ? userMessages[0] : null;
};

export const sendMessage = async ({
  question,
  question_id,
  tryAgain = false,
  scrollToEnd,
  type,
  tab_id,
}: SendMessageWithImageParams): Promise<boolean> => {
  try {
    const {
      id: userId,
      first_name,
      last_name,
      specialty_id,
    } = store.getState()?.auth?.currentUser || {};
    const {threadId, data} = store.getState().clinicalCase || {};
    let tempData = [...(data || [])];

    store.dispatch(setMessageIsLoading());
    if (!question) {
      throw new Error('Question text must be provided');
    }

    const newMessage: MessageItem = {
      id: new Date().toISOString(),
      message: question || '',
      role: 'user',
      sender_id: userId + '',
      sender_name: first_name + ' ' + last_name,
      thread_id: threadId + '',
      status: MessageStatus.ReadByEveryone,
      created_at: new Date().toISOString(),
    };

    if (!tryAgain) {
      tempData = [newMessage, ...tempData];
      store.dispatch(setClinicalCase({data: tempData}));
    }
    scrollToEnd?.();
    store.dispatch(
      setTypingStatus({status: {userId: 'genio', isTyping: true}}),
    );

    const body: QNAForm = {
      question: question || '',
      question_id: question_id,
      specialty_id: specialty_id || -1,
      type: type || ChatType.ClinicalCase,
      user_id: userId || -1,
      thread_id: threadId,
    };

    const messageId = dayjs().add(1, 'minute').toISOString();

    // Create an empty assistant message immediately to show the 3-phase loading indicator
    const emptyAssistantMessage: MessageItem = {
      id: messageId,
      message: '',
      role: 'assistant',
      sender_id: 'genio',
      sender_name: 'GENIO',
      thread_id: threadId + '',
      status: MessageStatus.ReadByEveryone,
      created_at: new Date().toISOString(),
    };
    tempData = [emptyAssistantMessage, ...tempData];
    store.dispatch(setClinicalCase({data: tempData}));

    store.dispatch(setStreamMessageLoadingId(messageId));

    // Throttle chat store updates
    const throttledDispatch = throttle(
      (updatedMessages: string) => {
        store.dispatch(
          setClinicalCase({
            data: [
              {
                message: cleanAndFormatMarkdown(updatedMessages || ''),
                sender_id: 'genio',
                sender_name: 'GENIO',
                role: 'assistant',
                thread_id: threadId + '',
                status: MessageStatus.ReadByEveryone,
                created_at: new Date().toISOString(),
                id: messageId,
              },
              ...tempData,
            ],
          }),
        );

        store.dispatch(
          setTypingStatus({status: {userId: 'genio', isTyping: false}}),
        );
      },
      100,
      {
        leading: true,
        trailing: true,
      },
    );

    let prevText = '';
    let accumulatedText = '';
    let num = 0;

    const res = await services.qna.sendStreamingQNA(
      body,
      newThreadId => {
        if (!threadId && newThreadId) {
          store.dispatch(setCurrentThreadId(newThreadId));
          getUserThreads(undefined, tab_id?.toString());
        }
      },
      accumulatedAnswer => {
        if (prevText.length - accumulatedText.length > 30) {
          accumulatedText = accumulatedAnswer;
          num += 1;
          store.dispatch(
            setClinicalCase({
              data: [
                {
                  message: cleanAndFormatMarkdown(accumulatedAnswer || ''),
                  sender_id: 'genio',
                  role: 'assistant',
                  sender_name: 'GENIO',
                  thread_id: threadId + '',
                  status: MessageStatus.ReadByEveryone,
                  created_at: new Date().toISOString(),
                  id: messageId,
                },
                ...tempData,
              ],
            }),
          );
          if (num === 1) {
            store.dispatch(
              setTypingStatus({status: {userId: 'genio', isTyping: false}}),
            );
          }
        }
        prevText = accumulatedAnswer;
      },
    );
    store.dispatch(
      setClinicalCase({
        data: [
          {
            message: cleanAndFormatMarkdown(prevText || ''),
            sender_id: 'genio',
            role: 'assistant',
            sender_name: 'GENIO',
            thread_id: threadId + '',
            status: MessageStatus.ReadByEveryone,
            created_at: new Date().toISOString(),
            id: messageId,
          },
          ...tempData,
        ],
      }),
    );

    // Flush any remaining updates after the loop
    throttledDispatch.flush();

    if (!res) {
      store.dispatch(setMessageIsFail());
      throw new Error('Error inserting message: ');
    }
    store.dispatch(setMessageSuccess());
    return true;
  } catch (error) {
    return false;
  } finally {
    store.dispatch(
      setTypingStatus({status: {userId: 'genio', isTyping: false}}),
    );
    finishLoading();
  }
};

export const onStreamingMessageEndAnimation = () => {
  const {streamMessageLoadingId} = store.getState().clinicalCase;
  if (streamMessageLoadingId) {
    store.dispatch(setStreamMessageLoadingId(null));
  }
};

export const resetClinicalCase = () => {
  store.dispatch(setClinicalCase({data: []}));
  store.dispatch(setCurrentThreadId(undefined));
  store.dispatch(setMessageSuccess());
};

export const updateClinicalCaseWithAdjustedStatuses = (
  messages?: MessageItem[],
) => {
  const clonedMessages = [...(messages || [])];
  let currentStatus: MessageStatus | null = null;

  for (let i = 0; i < clonedMessages.length; i++) {
    const message = clonedMessages[i];
    if (message.status === MessageStatus.ReadByEveryone) {
      currentStatus = MessageStatus.ReadByEveryone;
    } else if (
      message.status === MessageStatus.ReceivedNotRead &&
      !currentStatus
    ) {
      currentStatus = MessageStatus.ReceivedNotRead;
    }

    if (currentStatus) {
      clonedMessages[i] = {
        ...message,
        status: currentStatus,
      };
    }
  }

  store.dispatch(setClinicalCase({data: clonedMessages}));
};
